import React, { useState, useEffect } from 'react';
import { PublicHeader } from '../../components/public/PublicHeader';
import { PublicFooter } from '../../components/public/PublicFooter';
import { INITIAL_PROJECTS } from '../../data/mockData';
import { ProjectItem } from '../../types';
import { resolveProjectMedia } from '../../lib/mediaResolver';
import { getProjects } from '../../lib/cmsService';
import { ProjectDetailModal } from '../../components/public/ProjectDetailModal';
import { Film, Play, Camera, Globe, Sparkles, Filter, ArrowRight, Clapperboard, Calendar } from 'lucide-react';
import { StargazeImage } from '../../components/common/StargazeImage';

export const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectItem[]>(INITIAL_PROJECTS);
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [filter, setFilter] = useState<string>('ALL');

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        const cmsData = await getProjects();
        const base = cmsData && cmsData.length > 0 ? cmsData : INITIAL_PROJECTS;
        const resolved = await Promise.all(base.map(resolveProjectMedia));
        if (isMounted) setProjects(resolved);
      } catch (err) {
        console.warn('Error loading projects, using defaults:', err);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const publishedProjects = projects.filter((p) => p.status === 'PUBLISHED');

  const filteredProjects = publishedProjects.filter((p) => {
    if (filter === 'ALL') return true;
    const g = (p.genre || '').toUpperCase();
    if (filter === 'FILMS') return g.includes('DRAMA') || g.includes('FEATURE');
    if (filter === 'CONCERTS') return g.includes('MUSIC') || g.includes('CONCERT');
    if (filter === 'PUBLIC') return g.includes('PUBLIC') || g.includes('POLICE') || g.includes('AWARENESS');
    return true;
  });

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans selection:bg-[#D97706] selection:text-white">
      <PublicHeader />
      <main className="pt-32 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="border-b border-zinc-200 pb-8 space-y-4">
          <div className="inline-flex items-center gap-2 text-[#B45309] font-mono text-xs font-bold tracking-widest uppercase">
            <Film className="w-4 h-4 text-[#D97706]" /> COMPLETE STUDIO WORK & PORTFOLIO
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl sm:text-6xl font-black tracking-tight uppercase font-serif-cinematic text-zinc-900">
                STARGAZE WORK
              </h1>
              <p className="text-zinc-600 text-sm sm:text-base mt-2 max-w-2xl font-light">
                Curated productions across feature films, live musical spectacles, civic broadcast campaigns, and narrative shorts.
              </p>
            </div>

            {/* Cross-Section Inter-Links */}
            <div className="flex flex-wrap gap-2 text-xs font-mono">
              <a
                href="/films"
                className="px-3.5 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Clapperboard className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Films Slate</span>
              </a>
              <a
                href="/events"
                className="px-3.5 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Calendar className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Live Events</span>
              </a>
              <a
                href="/rental"
                className="px-3.5 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Camera className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Gear Rental</span>
              </a>
              <a
                href="/marketing"
                className="px-3.5 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
                <span>PR & Marketing</span>
              </a>
            </div>
          </div>

          {/* Filter Pills */}
          <div className="pt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono text-zinc-400 uppercase mr-2 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Filter Portfolio:
            </span>
            {[
              { id: 'ALL', label: 'All Productions' },
              { id: 'FILMS', label: 'Feature & Narrative' },
              { id: 'CONCERTS', label: 'Live Concerts & Tours' },
              { id: 'PUBLIC', label: 'Civic Campaigns' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-xs font-mono font-bold uppercase transition ${
                  filter === f.id
                    ? 'bg-[#D97706] text-white shadow-md shadow-amber-600/20'
                    : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              data-cursor="EXPLORE"
              className="bg-[#F8F9FA] border border-zinc-200 rounded-2xl overflow-hidden hover:border-amber-400 hover:shadow-xl transition flex flex-col justify-between cursor-pointer group"
            >
              <div>
                <div className="aspect-[16/9] relative overflow-hidden bg-zinc-900">
                  <StargazeImage
                    src={project.posterUrl}
                    alt={project.title}
                    focalPoint={project.focalPoint}
                    fallbackTitle={project.title}
                    category={project.genre}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute top-3 right-3 bg-[#D97706] text-white font-mono text-[10px] font-bold px-2.5 py-1 rounded uppercase shadow-sm">
                    {project.genre.split('/')[0]}
                  </span>

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 bg-black/40">
                    <div className="w-12 h-12 rounded-full bg-[#D97706] flex items-center justify-center text-white shadow-lg">
                      <Play className="w-5 h-5 fill-white translate-x-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition mb-2">
                    {project.title}
                  </h3>
                  <p className="text-xs text-zinc-600 leading-relaxed mb-4 line-clamp-3">
                    {project.synopsis}
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0 font-mono text-xs text-zinc-500 flex justify-between border-t border-zinc-200 pt-4 mt-2">
                <span>Director: <strong className="text-zinc-700">{project.director}</strong></span>
                <span className="font-bold text-[#B45309]">{project.releaseYear}</span>
              </div>
            </div>
          ))}
        </div>
      </main>

      <ProjectDetailModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
      <PublicFooter />
    </div>
  );
};
